/**
 * Queue tilt ribbon (TradeFinder Context): one cell per displayed bar along
 * the bottom edge of the price pane, coloured by the bar's mean queue tilt
 * (green = bid queue heavier, red = ask) with the band's opacity scale (faint
 * near zero, full from |tilt| 0.5). An MES chart gets a second, thinner row
 * above it for ES's book. Data: queueTilt.ts `tiltCells`.
 *
 * Not a glyph, so not part of the marker layout; it owns a few px at the very
 * bottom of the pane and draws at zOrder 'top' so a low candle never hides it.
 */
import type {
  IChartApi,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  ISeriesPrimitive,
  PrimitivePaneViewZOrder,
  SeriesAttachedParameter,
  Time,
} from 'lightweight-charts';
import { queueTiltStyle, type TiltCell } from './queueTilt';

/** Row heights, CSS px (primary / secondary), the gap between rows and the bottom pad. */
export const RIBBON_PRIMARY_PX = 6;
export const RIBBON_PRIMARY_COMPACT_PX = 5;
export const RIBBON_SECONDARY_PX = 3;
export const RIBBON_GAP_PX = 1;
export const RIBBON_BOTTOM_PX = 1;

export interface RibbonRow {
  cells: readonly TiltCell[];
  /** Row height, CSS px. */
  height: number;
}

/** One rectangle to fill, CSS px (y from the pane top). */
export interface RibbonRect {
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
  alpha: number;
}

interface CanvasLike {
  globalAlpha: number;
  fillStyle: string;
  fillRect(x: number, y: number, w: number, h: number): void;
}

type ChartLike = Pick<IChartApi, 'timeScale'>;

/**
 * Rectangles for the rows (row 0 at the bottom), given the pane height. Cells
 * whose bar is not on the chart (timeToCoordinate null) are skipped; each cell
 * spans one bar spacing centred on its bar.
 */
export function ribbonRects(
  chart: ChartLike,
  rows: readonly RibbonRow[],
  paneHeight: number,
  colors: { high: string; low: string },
): RibbonRect[] {
  const out: RibbonRect[] = [];
  if (!(paneHeight > 0)) return out;
  const ts = chart.timeScale();
  const spacing = Math.max(1, ts.options().barSpacing);
  let bottom = paneHeight - RIBBON_BOTTOM_PX;
  for (const row of rows) {
    const y = bottom - row.height;
    for (const c of row.cells) {
      const x = ts.timeToCoordinate(c.time as Time);
      if (x === null) continue;
      const st = queueTiltStyle(c.value, colors);
      out.push({ x: x - spacing / 2, y, w: spacing, h: row.height, color: st.color, alpha: st.opacity });
    }
    bottom = y - RIBBON_GAP_PX;
  }
  return out;
}

class RibbonRenderer implements IPrimitivePaneRenderer {
  constructor(private readonly src: QueueTiltRibbonPrimitive) {}

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  draw(target: any): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasLike;
      const hr = scope.horizontalPixelRatio as number;
      const vr = scope.verticalPixelRatio as number;
      const height = (scope.bitmapSize.height as number) / vr;
      const rects = this.src.rects(height);
      const prev = ctx.globalAlpha;
      for (const r of rects) {
        ctx.globalAlpha = r.alpha;
        ctx.fillStyle = r.color;
        // whole device px, and cells touch: round both edges, not the width
        const x0 = Math.round(r.x * hr);
        const x1 = Math.round((r.x + r.w) * hr);
        ctx.fillRect(x0, Math.round(r.y * vr), Math.max(1, x1 - x0), Math.max(1, Math.round(r.h * vr)));
      }
      ctx.globalAlpha = prev;
    });
  }
}

class RibbonView implements IPrimitivePaneView {
  private readonly _renderer: RibbonRenderer;
  constructor(src: QueueTiltRibbonPrimitive) {
    this._renderer = new RibbonRenderer(src);
  }
  zOrder(): PrimitivePaneViewZOrder {
    return 'top';
  }
  renderer(): IPrimitivePaneRenderer {
    return this._renderer;
  }
}

export class QueueTiltRibbonPrimitive implements ISeriesPrimitive<Time> {
  private _chart: ChartLike | null = null;
  private _request: (() => void) | null = null;
  private _rows: readonly RibbonRow[] = [];
  private _colors = { high: '#ff5370', low: '#2ee6a6' };
  private readonly _view = new RibbonView(this);

  attached(p: SeriesAttachedParameter<Time>): void {
    this._chart = p.chart;
    this._request = p.requestUpdate;
  }

  detached(): void {
    this._chart = null;
    this._request = null;
  }

  updateAllViews(): void {}

  paneViews(): readonly IPrimitivePaneView[] {
    return [this._view];
  }

  setData(rows: readonly RibbonRow[], colors: { high: string; low: string }): void {
    this._rows = rows;
    this._colors = colors;
    this._request?.();
  }

  /** Cells drawn (all rows), for the headless hook. */
  cellCount(): number {
    return this._rows.reduce((n, r) => n + r.cells.length, 0);
  }

  rects(paneHeight: number): RibbonRect[] {
    return this._chart ? ribbonRects(this._chart, this._rows, paneHeight, this._colors) : [];
  }
}
